import { DateValue } from '@internationalized/date';
import { CrmAggregateType } from '@rematter/frontend-lib-api-client';
import { atom, useAtom } from 'jotai';

import { crmOutboxEventCountsQueryAtom } from '../_graphql/queryCrmOutboxEventCounts';
import { crmOutboxEventsQueryAtom } from '../_graphql/queryCrmOutboxEvents';

import {
  dateToZonedDateTimeEnd,
  dateToZonedDateTimeStart,
} from '~/shared/utils/dateTime';

export type CrmAuditTab = 'events' | 'inbox';

export const $crmAuditTab = atom<CrmAuditTab>('events');

export const $crmAggregateTypeFilter = atom<CrmAggregateType | undefined>(
  undefined,
);

export const $crmAggregateIdFilter = atom<string | undefined>(undefined);

export const $crmPagination = atom<{ offset: number; limit: number }>({
  offset: 0,
  limit: 50,
});

export const $selectedCrmEventId = atom<string | undefined>(undefined);

export const $crmDateRangeFilter = atom<{
  start: DateValue;
  end: DateValue;
} | null>(null);

export const $remoteCrmOutboxEvents = crmOutboxEventsQueryAtom((get) => {
  const aggregateType = get($crmAggregateTypeFilter);
  const aggregateId = get($crmAggregateIdFilter);
  const pagination = get($crmPagination);
  const dateRange = get($crmDateRangeFilter);

  return {
    variables: {
      aggregateTypes: aggregateType ? [aggregateType] : undefined,
      aggregateId,
      pagination,
      createdAtRange:
        dateRange?.start && dateRange?.end
          ? {
              start: dateToZonedDateTimeStart(dateRange.start),
              end: dateToZonedDateTimeEnd(dateRange.end),
            }
          : undefined,
    },
  };
});

export const $remoteCrmOutboxEventCounts = crmOutboxEventCountsQueryAtom();

export const useCrmAuditTab = () => {
  const [tab, setTab] = useAtom($crmAuditTab);
  return [tab, setTab] as const;
};

export const useCrmAggregateTypeFilter = () => {
  const [filter, setFilter] = useAtom($crmAggregateTypeFilter);
  const [, setAggregateId] = useAtom($crmAggregateIdFilter);

  const setFilterAndResetId = (value: CrmAggregateType | undefined) => {
    setFilter(value);
    setAggregateId(undefined);
  };

  return [filter, setFilterAndResetId] as const;
};

export const useCrmAggregateIdFilter = () => {
  const [filter, setFilter] = useAtom($crmAggregateIdFilter);
  return [filter, setFilter] as const;
};

export const useCrmPagination = () => {
  const [pagination, setPagination] = useAtom($crmPagination);
  return [pagination, setPagination] as const;
};

export const useSelectedCrmEventId = () => {
  const [selectedId, setSelectedId] = useAtom($selectedCrmEventId);
  return [selectedId, setSelectedId] as const;
};

export const useCrmDateRangeFilter = () => {
  const [filter, setFilter] = useAtom($crmDateRangeFilter);
  return [filter, setFilter] as const;
};
